/**
 * 指挥页轨迹保存 / 校验 / 导出格式化（从 Dashboard.js 拆出）
 */

export function validateCoordinates(lat, lng) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return false;
  if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
}

export function createTrackSessionId(memberName = 'track') {
  return `${memberName}_${Date.now()}`;
}

/** 轨迹点转为 Mapbox LineString 坐标 [[lng,lat], ...] */
export function buildTrackLineCoordinates(trackData = []) {
  return (trackData || [])
    .filter((p) => validateCoordinates(Number(p.lat), Number(p.lng)))
    .map((p) => [Number(p.lng), Number(p.lat)]);
}

export function saveTrackPointToLocalStorage(member, point, sessionId) {
  try {
    const key = `track_backup_${member.name}_${sessionId}`;
    const existingData = JSON.parse(localStorage.getItem(key) || '[]');
    existingData.push({
      ...point,
      member_name: member.name,
      device_unique_id: member.device_unique_id,
      timestamp: point.timestamp,
      track_session_id: sessionId
    });
    localStorage.setItem(key, JSON.stringify(existingData));
    console.log(`📦 轨迹点已保存到localStorage备份: ${member.name}`);
  } catch (error) {
    console.error('保存轨迹点到localStorage失败:', error);
  }
}

export async function saveTrackPointToDatabase(member, point, sessionId) {
  try {
    const response = await fetch('/api/track/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        member_name: member.name,
        device_unique_id: member.device_unique_id,
        latitude: point.lat,
        longitude: point.lng,
        altitude: member.last_altitude || null,
        accuracy: member.last_accuracy || null,
        speed: member.last_speed || null,
        bearing: member.last_bearing || null,
        timestamp: point.timestamp,
        track_session_id: sessionId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    if (result.success) {
      console.log(`✅ 轨迹点已保存到数据库: ${member.name} - ${point.timestamp}`);
    } else {
      console.error('保存轨迹点失败:', result.message);
    }
  } catch (error) {
    console.error('保存轨迹点到数据库失败:', error);
    saveTrackPointToLocalStorage(member, point, sessionId);
  }
}

/**
 * @returns {{ content: string, filename: string } | null}
 */
export function formatTrackExport(memberName, trackData, format = 'gpx') {
  if (!trackData || trackData.length === 0) return null;
  let exportData = '';
  let filename = `track_${memberName}_${new Date().toISOString().split('T')[0]}`;

  switch (String(format).toLowerCase()) {
    case 'gpx':
      exportData = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Rescue Team Tracker" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${memberName} 轨迹数据</name>
    <time>${new Date().toISOString()}</time>
  </metadata>
  <trk>
    <name>${memberName} 轨迹</name>
    <trkseg>
${trackData.map((point) => `      <trkpt lat="${point.lat}" lon="${point.lng}">
        <time>${point.timestamp}</time>
      </trkpt>`).join('\n')}
    </trkseg>
  </trk>
</gpx>`;
      filename += '.gpx';
      break;
    case 'kml':
      exportData = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${memberName} 轨迹数据</name>
    <Placemark>
      <name>${memberName} 轨迹</name>
      <LineString>
        <coordinates>
${trackData.map((point) => `          ${point.lng},${point.lat},0`).join('\n')}
        </coordinates>
      </LineString>
    </Placemark>
  </Document>
</kml>`;
      filename += '.kml';
      break;
    case 'json':
      exportData = JSON.stringify({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {
            name: memberName,
            timestamp: new Date().toISOString(),
            pointCount: trackData.length
          },
          geometry: {
            type: 'LineString',
            coordinates: trackData.map((point) => [point.lng, point.lat])
          }
        }]
      }, null, 2);
      filename += '.json';
      break;
    case 'csv':
      exportData = '时间,纬度,经度\n';
      exportData += trackData.map((point) => `${point.timestamp},${point.lat},${point.lng}`).join('\n');
      filename += '.csv';
      break;
    default:
      return null;
  }

  return { content: exportData, filename };
}

export function downloadTextFile(content, filename) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
